import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Pencil, Save } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { LegalBlocks } from "./legalContent";

const API = process.env.REACT_APP_BACKEND_URL;

/** Admin → Εργαλεία: επεξεργασία + εμφάνιση/απόκρυψη των κειμένων της αρχικής σελίδας (login). */
const LegalDocsCard = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // {key,title,updated,content}
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/legal-docs`);
      setDocs(r.data?.docs || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης κειμένων");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const update = async (key, payload, okMsg) => {
    const r = await axios.put(`${API}/api/admin/legal-docs/${key}`, payload);
    setDocs((ds) => ds.map((d) => (d.key === key ? r.data : d)));
    toast.success(okMsg);
    return r.data;
  };

  const toggleVisible = async (d) => {
    try { await update(d.key, { visible: !d.visible }, !d.visible ? "Εμφανίζεται στην αρχική σελίδα" : "Αποκρύφθηκε από την αρχική σελίδα"); }
    catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };

  const save = async () => {
    if (!edit.title.trim()) return toast.error("Ο τίτλος είναι υποχρεωτικός");
    setSaving(true);
    try {
      await update(edit.key, { title: edit.title.trim(), updated: edit.updated, content: edit.content }, "Το κείμενο αποθηκεύτηκε");
      setEdit(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα αποθήκευσης"); }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="legal-docs-card">
      <CardHeader>
        <CardTitle className="text-base">Κείμενα Αρχικής Σελίδας</CardTitle>
        <CardDescription>Δήλωση Συμμόρφωσης, Ασφάλεια Ιστότοπου, Όροι Χρήσης, Πολιτική Απορρήτου — εμφανίζονται αναπτυσσόμενα κάτω από το μότο στη σελίδα σύνδεσης. Επιλέξτε ποια εμφανίζονται και επεξεργαστείτε το περιεχόμενό τους.</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
          <div className="divide-y rounded-lg border">
            {docs.map((d) => (
              <div key={d.key} className="flex items-center gap-4 p-3" data-testid={`legal-doc-row-${d.key}`}>
                <Switch checked={!!d.visible} onCheckedChange={() => toggleVisible(d)} data-testid={`legal-doc-visible-${d.key}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate" data-testid={`legal-doc-title-${d.key}`}>{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">{d.updated || "—"}{d.updated_by && d.updated_by !== "seed" ? ` · τελευταία αλλαγή: ${d.updated_by}` : ""}</div>
                </div>
                <Badge variant={d.visible ? "default" : "secondary"} className={d.visible ? "bg-emerald-600" : ""} data-testid={`legal-doc-status-${d.key}`}>
                  {d.visible ? <><Eye className="w-3 h-3 mr-1" />Εμφανίζεται</> : <><EyeOff className="w-3 h-3 mr-1" />Κρυφό</>}
                </Badge>
                <Button size="sm" variant="outline" onClick={() => { setPreview(false); setEdit({ key: d.key, title: d.title, updated: d.updated || "", content: d.content || "" }); }} data-testid={`legal-doc-edit-${d.key}`}>
                  <Pencil className="w-4 h-4 mr-1" />Επεξεργασία
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="legal-doc-dialog">
          <DialogHeader><DialogTitle>Επεξεργασία κειμένου</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Τίτλος</Label>
                  <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="legal-doc-title-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Γραμμή ημερομηνίας / τροποποίησης (προαιρετικό)</Label>
                  <Input value={edit.updated} onChange={(e) => setEdit({ ...edit, updated: e.target.value })} placeholder="π.χ. Η συγκεκριμένη ενότητα τροποποιήθηκε στις: 5/10/2026" data-testid="legal-doc-updated-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label>Περιεχόμενο</Label>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setPreview(!preview)} data-testid="legal-doc-preview-toggle">
                    {preview ? "Επεξεργασία" : "Προεπισκόπηση"}
                  </Button>
                </div>
                {preview ? (
                  <div className="rounded-lg border bg-slate-50 px-4 py-3 text-sm text-slate-700 max-h-[50vh] overflow-y-auto" data-testid="legal-doc-preview">
                    <LegalBlocks content={edit.content} />
                  </div>
                ) : (
                  <Textarea rows={18} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} className="font-mono text-sm" data-testid="legal-doc-content-input" />
                )}
                <p className="text-xs text-slate-500">Μορφοποίηση: γραμμή που αρχίζει με <code>## </code> = επικεφαλίδα ενότητας · γραμμή με <code>- </code> = στοιχείο λίστας · κενή γραμμή = νέα παράγραφος.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)} data-testid="legal-doc-cancel">Άκυρο</Button>
            <Button onClick={save} disabled={saving} data-testid="legal-doc-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Αποθήκευση
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default LegalDocsCard;
